import { FileTextOutlined, HomeOutlined, LaptopOutlined, PhoneOutlined } from '@ant-design/icons';
import { useResponsive } from 'ahooks';
import { Card, Descriptions, DescriptionsProps, Space, Spin, Tag, Typography } from 'antd';
import { CardTabListType } from 'antd/es/card';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BookingType } from '@/models';
import { formatResourceName, showGender } from '@/utils';
import { useResourceMe } from '@/web-api/resources';

import { ResourceDocuments } from '../components/ResourceDocuments';
import { ResourceLocations } from '../components/ResourceLocations';

const { Text } = Typography;

export function AppProfilePage() {
  const { t } = useTranslation();
  const breakpoint = useResponsive();
  const { data: resource, isLoading } = useResourceMe();
  const [activeTabKey, setActiveTabKey] = useState('profile');

  const onTabChange = (key: string) => {
    setActiveTabKey(key);
  };

  if (isLoading) {
    return (
      <Card
        title={t('header:yourProfile')}
        styles={{ body: { overflowY: 'auto', maxHeight: '75vh' } }}
      >
        <Spin size='large' style={{ display: 'block', textAlign: 'center', padding: '2rem' }} />
      </Card>
    );
  }

  if (!resource) {
    return (
      <Card
        title={t('header:yourProfile')}
        styles={{ body: { overflowY: 'auto', maxHeight: '75vh' } }}
      >
        <Text>{t('common:noData')}</Text>
      </Card>
    );
  }

  const bookingTypeIcons: Record<BookingType, { icon: typeof HomeOutlined; rotate?: number }> = {
    [BookingType.ONSITE]: { icon: HomeOutlined },
    [BookingType.REMOTE]: { icon: LaptopOutlined },
    [BookingType.PHONE]: { icon: PhoneOutlined, rotate: 90 },
    [BookingType.DOCUMENT]: { icon: FileTextOutlined },
  };

  const items: DescriptionsProps['items'] = [
    {
      key: 'name',
      label: t('forms:name'),
      children: (
        <Text>
          {formatResourceName(resource.firstname, resource.lastname, 'non-formal')}&nbsp;(
          {showGender(resource.gender)})
        </Text>
      ),
    },
    {
      key: 'number',
      label: t('common:resourceNumber'),
      children: resource.number.toString(),
    },
    {
      key: 'email',
      label: t('forms:email'),
      children: resource.email,
    },
    {
      key: 'phone',
      label: t('forms:phone'),
      children: resource.phone,
    },
    {
      key: 'bookingTypes',
      label: t('common:bookingType'),
      children: (
        <Space size='small' wrap>
          {resource.bookingTypes.map((type) => {
            const config = bookingTypeIcons[type];
            if (!config) return null;
            const Icon = config.icon;
            return (
              <Tag
                key={type}
                icon={
                  <Icon style={{ rotate: config.rotate ? `${config.rotate}deg` : undefined }} />
                }
              >
                {t(`common:${type}`)}
              </Tag>
            );
          })}
        </Space>
      ),
    },
    {
      key: 'languages',
      label: t('common:languages'),
      children: (
        <Space size='small' wrap>
          {resource.languages?.map((language) => (
            <Tag key={language.id}>{language.name}</Tag>
          ))}
        </Space>
      ),
    },
    ...(resource.street || resource.zip || resource.city
      ? [
          {
            key: 'address',
            label: t('forms:address'),
            children: [resource.street, resource.zip, resource.city, resource.country]
              .filter(Boolean)
              .join(', '),
          },
        ]
      : []),
    ...(resource.dateOfBirth
      ? [
          {
            key: 'dateOfBirth',
            label: t('forms:birthdate'),
            children: resource.dateOfBirth,
          },
        ]
      : []),
    ...(resource.placeOfBirth
      ? [
          {
            key: 'placeOfBirth',
            label: t('forms:placeOfBirth'),
            children: resource.placeOfBirth,
          },
        ]
      : []),
    ...(resource.originCountry
      ? [
          {
            key: 'originCountry',
            label: t('forms:originCountry'),
            children: resource.originCountry,
          },
        ]
      : []),
    ...(resource.contractStartDate
      ? [
          {
            key: 'contractStartDate',
            label: t('forms:contractStartDate'),
            children: resource.contractStartDate,
          },
        ]
      : []),
    ...(resource.contractEndDate
      ? [
          {
            key: 'contractEndDate',
            label: t('forms:contractEndDate'),
            children: resource.contractEndDate,
          },
        ]
      : []),
    ...(resource.taxId
      ? [
          {
            key: 'taxId',
            label: t('forms:taxId'),
            children: resource.taxId,
          },
        ]
      : []),
    ...(resource.taxNumber
      ? [
          {
            key: 'taxNumber',
            label: t('forms:taxNumber'),
            children: resource.taxNumber,
          },
        ]
      : []),
  ];

  const tabList: CardTabListType[] = [
    {
      key: 'profile',
      tab: t('header:yourProfile'),
    },
    {
      key: 'locations',
      tab: t('common:locations'),
    },
    {
      key: 'documents',
      tab: t('common:documents'),
    },
  ];

  const contentList: Record<string, React.ReactNode> = {
    profile: (
      <Descriptions
        size='small'
        bordered
        layout={breakpoint.lg ? 'horizontal' : 'vertical'}
        items={items}
        styles={{ label: { fontWeight: 'bolder' } }}
        column={1}
      />
    ),
    locations: <ResourceLocations resourceLocations={resource.locations} />,
    documents: <ResourceDocuments resourceId={resource.id} />,
  };

  return (
    <Card
      title={formatResourceName(resource.firstname, resource.lastname, 'non-formal')}
      styles={{ body: { overflowY: 'auto', maxHeight: '75vh' } }}
      tabList={tabList}
      tabProps={{ size: 'small' }}
      activeTabKey={activeTabKey}
      onTabChange={onTabChange}
    >
      {contentList[activeTabKey]}
    </Card>
  );
}
